import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subject, takeUntil } from 'rxjs';
import { SubscriberService } from 'src/app/services/manager/subscriber.service';
import { CurrentOrderService } from 'src/app/services/manager/current-order.service';
import { OrderService } from 'src/app/services/manager/order.service';
import { AlertService } from 'src/app/services/base/alert.service';
import { LoginService } from 'src/app/services/auth/login.service';
import { ImageCacheService } from 'src/app/services/data/image-cache.service';
import { StorageService } from 'src/app/services/data/storage.service';
import { ImageLightboxComponent } from 'src/app/components/order/image-lightbox/image-lightbox.component';

const COLOR_MAP: Record<string, string> = {
  'BIANCO': '#FFFFFF', 'GESSO': '#F5F5F0', 'NATURALE': '#F5F0E8', 'PANNA': '#FFF8E7',
  'AVORIO': '#FFFFF0', 'BURRO': '#FFF8DC', 'PERLA': '#EEE8D5', 'NUDE': '#E8C9A0',
  'GHIACCIO': '#E0F0FF', 'LATTE': '#FFF5E4', 'MARGARINA': '#FFFACD',
  'GRIGIO': '#9E9E9E', 'GRIGIO CHIARO': '#D3D3D3', 'GRIGIO MEDIO': '#808080',
  'GRIGIO SCURO': '#555555', 'GRIGIO MELANGE': '#A8A8A8', 'GRIGIO MEDIO MELANGE': '#888888',
  'ANTRACITE': '#3B3B3B', 'GRAPHITE': '#474747', 'PIOMBO': '#696969',
  'LAVAGNA': '#2F2F2F', 'CEMENTO': '#8B8682', 'LAVAGGIO SCURO': '#3A3A4A',
  'NERO': '#111111', 'MORO': '#0A0A0A',
  'BEIGE': '#F5F5DC', 'SABBIA': '#C2B280', 'CORDA': '#8B7355', 'CORDA MELANGE': '#9B8365',
  'TABACCO': '#7B4F2E', 'NOCCIOLA': '#8B6343', 'CAMMELLO': '#C19A6B',
  'CIOCCOLATO': '#5C3317', 'FANGO': '#7A6652', 'TAUPE': '#8B7D6B',
  'TORTORA': '#9E9585', 'MASTICE': '#D4C5A9', 'COCCIO': '#B5651D',
  'ROSSO': '#CC0000', 'AMARONE': '#8B1A1A', 'AMARANTO': '#9B2335',
  'BORDEAUX': '#6D1A2A', 'BURGUNDY': '#7D1628', 'VINO': '#722F37', 'MOSTO': '#4A235A',
  'PRUGNA': '#5C2A5A', 'MELANZANA': '#4E2A4E', 'RUGGINE': '#8B4513',
  'ARANCIONE': '#FF6600', 'SENAPE': '#CCAA00', 'KAKY': '#8B864E',
  'GIALLO': '#FFD700', 'OCRA': '#CC7722', 'ORO': '#C8A400', 'LIMONE': '#FFF44F',
  'VERDE': '#2E7D32', 'VERDE MILITARE': '#4B5320', 'MILITARE': '#4B5320',
  'SALVIA': '#8FAF7E', 'VERDE SALVIA': '#8FAF7E', 'BOTTIGLIA': '#2C5F2E',
  'KAKI': '#8B864E', 'MENTA': '#98FF98', 'ACQUA': '#00CFCF', 'PISTACCHIO': '#93C572',
  'BLU': '#003580', 'NAVY': '#001F5B', 'DENIM': '#1560BD', 'JEANS': '#1A6BAA',
  'AVIO': '#5B7FA6', 'BLU AVIO': '#5B7FA6', 'BLUETTE': '#5B7FA6',
  'AZZURRO': '#0080FF', 'CELESTE': '#87CEEB',
  'INDACO': '#4B0082', 'COBALTO': '#0047AB', 'OTTANIO': '#2D6B7A',
  'VIOLA': '#7F00FF', 'LAVANDA': '#9B89C4', 'LILLA': '#9B89C4',
  'MALVA': '#9E7BB5', 'AMETISTA': '#9966CC',
  'ROSA': '#FFB6C1', 'CIPRIA': '#F4C2C2', 'SILVER PINK': '#C4AEAD',
  'FUCSIA': '#FF00AA', 'SALMONE': '#FA8072', 'CORALLO': '#FF6B6B',
};

function colorHexFallback(product: any): string | null {
  if (product.colorHex) return product.colorHex;
  const slug: string = (product.color || '').toUpperCase();
  if (COLOR_MAP[slug]) return COLOR_MAP[slug];
  const name: string = (product.colorname || '').toUpperCase();
  if (COLOR_MAP[name]) return COLOR_MAP[name];
  const first = name.split(' ')[0];
  if (first && COLOR_MAP[first]) return COLOR_MAP[first];
  for (const part of slug.split(/[-_]/)) {
    if (part && COLOR_MAP[part.trim()]) return COLOR_MAP[part.trim()];
  }
  return null;
}

@Component({
  selector: 'app-order-overview',
  templateUrl: './order-overview.component.html',
  styleUrls: ['./order-overview.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderOverviewComponent implements OnInit, OnDestroy {
  destroy = new Subject();

  products: any[] = [];
  computedProducts: any[] = [];
  adminMode: boolean = false;
  isTableView: boolean = true;
  isEditMode: boolean = false;
  canEdit: boolean = false;
  currentOrderId: string | null = null;

  /** Live-Abgleich: Key (karmaId bzw. model::colorname) -> aktuell nicht im Karma-Katalog. */
  private karmaUnavailableLookup = new Map<string, boolean>();

  colorHexFallback = colorHexFallback;

  totals = {
    totalQuantity: 0,
    totalPrice: 0,
    totalCost: 0,
    totalProfit: 0,
  };

  totalsTax = {
    netPrice: 0,
    taxAmount: 0,
    totalPrice: 0,
  };

  constructor(
    private sub: SubscriberService,
    private current: CurrentOrderService,
    private order: OrderService,
    private alert: AlertService,
    private login: LoginService,
    private imageCache: ImageCacheService,
    private modalCtrl: ModalController,
    private storage: StorageService,
    private cdr: ChangeDetectorRef
  ) {}

  get imagesEnabled(): boolean {
    return this.imageCache.enabled;
  }

  /**
   * Karma liefert nur EIN Produktfoto pro Modell — es zeigt nicht die bestellte Farbe.
   * Graustufen nehmen dem Foto die Farbaussage; die Farbe steht als Farbkaestchen
   * und Text daneben. Gleicher Schalter wie im PDF.
   */
  grayscaleImages = true;

  /** Lädt das Thumbnail einer Produktzeile über den Bild-Cache (Proxy + Data-URL). */
  loadThumb(product: any) {
    if (product._thumbUrl || product._thumbLoading) return;
    const url = product.images?.[0];
    if (!url || !this.imageCache.enabled) return;

    product._thumbLoading = true;
    this.imageCache.resolve(url).then((dataUrl) => {
      product._thumbUrl = dataUrl;
      product._thumbLoading = false;
      this.cdr.markForCheck();
    });
  }

  async openLightbox(product: any) {
    const images: string[] = product.images || [];
    if (!images.length) return;

    const modal = await this.modalCtrl.create({
      component: ImageLightboxComponent,
      componentProps: { images, startIndex: 0 },
      cssClass: 'lightbox-modal',
    });
    await modal.present();
  }

  ngOnInit() {
    this.subscribe();

    // OnPush: nach dem async Laden explizit neu pruefen lassen
    this.storage.getData<boolean>('pdfGrayscaleImages').then(value => {
      this.grayscaleImages = value !== false;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.destroy.next(null);
    this.destroy.complete();
  }
  
  /**
   * Check if the current user is the owner of the order
   * @returns true if the user is the owner of the order
   */
  isOrderOwner(): boolean {
    const currentOrder = this.sub.currentOrder.getValue();
    if (!this.login.decodedToken || !currentOrder?.owner) {
      return false;
    }

    const owner = currentOrder.owner;
    const ownerEmail = typeof owner === 'string' ? owner : owner?.email;
    return ownerEmail === this.login.decodedToken.email;
  }
  
  /**
   * Check if the current user is an admin
   * @returns true if the user is an admin
   */
  isAdmin(): boolean {
    return this.login.decodedToken?.role === 'admin';
  }
  
  /**
   * Check if standard order actions should be visible
   * @returns true if user is admin (always) OR user is owner and order is open
   */
  showStandardActions(): boolean {
    // Admins can always edit orders, even closed ones
    if (this.isAdmin()) {
      return true;
    }
    
    // Owners can only edit open orders
    const currentOrder = this.sub.currentOrder.getValue();
    const orderStateIsZeroOrUndefined = currentOrder?.state === 0 || currentOrder?.state === undefined || currentOrder?.state === null;
    return this.isOrderOwner() && orderStateIsZeroOrUndefined;
  }

  subscribe() {
    this.sub.currentOrder
      .pipe(takeUntil(this.destroy))
      .subscribe((data: any) => {
        if (data) {
          this.products = data.products || [];
          this.currentOrderId = data.orderId ?? null;
          this.canEdit = this.showStandardActions();
          this.recomputeTotals();
          this.cdr.markForCheck();
        }
      });

    this.sub.productList.pipe(takeUntil(this.destroy)).subscribe((data: any[]) => {
      this.rebuildKarmaUnavailableLookup(data || []);
      this.cdr.markForCheck();
    });

    this.sub.adminMode.pipe(takeUntil(this.destroy)).subscribe((data: any) => {
      if (data) {
        this.adminMode = data;
        this.cdr.markForCheck();
      }
    });
  }

  /** Baut den karmaId/model+colorname -> karmaUnavailable Lookup aus der aktuellen Produktliste. */
  private rebuildKarmaUnavailableLookup(productList: any[]) {
    const lookup = new Map<string, boolean>();
    for (const p of productList) {
      const unavailable = p?.karmaUnavailable === true;
      if (p?.karmaId) lookup.set(`id:${p.karmaId}`, unavailable);
      if (p?.model && p?.colorname) lookup.set(`mc:${p.model}::${p.colorname}`, unavailable);
    }
    this.karmaUnavailableLookup = lookup;
  }

  /** Live-Verfügbarkeit einer Order-Position: Snapshot-Wert falls vorhanden, sonst Live-Lookup. */
  isKarmaUnavailable(product: any): boolean {
    if (product?.karmaUnavailable === true) return true;
    if (product?.karmaId && this.karmaUnavailableLookup.has(`id:${product.karmaId}`)) {
      return this.karmaUnavailableLookup.get(`id:${product.karmaId}`)!;
    }
    if (product?.model && product?.colorname) {
      const key = `mc:${product.model}::${product.colorname}`;
      if (this.karmaUnavailableLookup.has(key)) return this.karmaUnavailableLookup.get(key)!;
    }
    return false;
  }

  karmaUnavailableSince(product: any): string | null {
    return product?.karmaUnavailableSince ?? null;
  }

  private recomputeTotals() {
    const base = Array.isArray(this.products) ? this.products : [];

    const computed = base.map((product: any) => {
      const sizes = Array.isArray(product?.sizes) ? product.sizes : [];
      const totalQuantity = sizes.reduce((sum: number, s: any) => sum + (Number(s?.quantity) || 0), 0);
      const price = Number(product?.price) || 0;
      const cost = Number(product?.cost) || 0;
      const totalPrice = totalQuantity * price;
      const totalCost = totalQuantity * cost;
      const profit = totalPrice - totalCost;

      // Geladene Thumbnails über Neuberechnungen hinweg behalten — sonst flackert
      // bei jeder Mengenänderung die gesamte Bildspalte neu auf.
      const previous = this.computedProducts.find((p: any) => p.modelId === product?.modelId);

      return {
        ...product,
        _totalQuantity: totalQuantity,
        _totalPrice: totalPrice,
        _totalCost: totalCost,
        _profit: profit,
        _thumbUrl: previous?._thumbUrl ?? null,
        _thumbLoading: false,
      };
    });

    const totalQuantity = computed.reduce((sum: number, p: any) => sum + (Number(p?._totalQuantity) || 0), 0);
    const totalPrice = computed.reduce((sum: number, p: any) => sum + (Number(p?._totalPrice) || 0), 0);
    const totalCost = computed.reduce((sum: number, p: any) => sum + (Number(p?._totalCost) || 0), 0);
    const totalProfit = totalPrice - totalCost;

    this.computedProducts = computed;
    this.totals = { totalQuantity, totalPrice, totalCost, totalProfit };

    for (const product of computed) {
      this.loadThumb(product);
    }

    const taxAmount = totalPrice * 0.19;
    const grossPrice = totalPrice + taxAmount;
    this.totalsTax = {
      netPrice: totalPrice,
      taxAmount,
      totalPrice: grossPrice,
    };
  }

  // Bearbeiten eines Produkts
  editProduct(selectedProduct: any) {
    const productsWithSameModel = this.products.filter(
      (product) => product.model === selectedProduct.model
    );

    this.order.openProductModal(productsWithSameModel);
  }

  trackByProductId(index: number, product: any): string {
    return product.modelId || index.toString();
  }

  trackBySize(index: number, size: any): string {
    return size.size ?? index.toString();
  }

  // Entfernen eines Produkts
  async removeProduct(product: any) {
    let name = product.name.slice(0, 40) + '... wirklich entfernen?';

    const result = await this.alert.presentConfirmationAlert(
      'Element entfernen',
      name
    );

    if (!result) {
      return;
    }

    product.info = null;
    let productId = product.modelId;

    let tempProducts = this.products.map((product) => {
      if (product.modelId === productId) {
        // Alle Größen auf 0 setzen
        product.sizes = product.sizes.map((size: any) => ({
          ...size,
          quantity: 0,
        }));
      }
      return product;
    });
    this.current.addOrUpdateProducts(tempProducts);
  }

  // Bearbeiten von Notizen
  async editNotes(product: any) {

      const response = await this.alert.presentTextInputAlert(
        'Info ändern',
        'Info eingeben:',
        product.info || '',
        'Speichern',
        'Abbrechen'
      );

      if (response !== null) {
        // Erstelle eine deep copy des Produkts mit der neuen Info
        const updatedProducts = this.products.map((p: any) => {
          if (p.modelId === product.modelId) {
            return { ...p, info: response };
          }
          return p;
        });
        this.current.addOrUpdateProducts(updatedProducts);
      }
  }

  // Vom Karma-Unavailable-Badge automatisch ergänzter Info-Text
  onKarmaInfoUpdated(product: any, newInfo: string) {
    const updatedProducts = this.products.map((p: any) => {
      if (p.modelId === product.modelId) {
        return { ...p, info: newInfo };
      }
      return p;
    });
    this.current.addOrUpdateProducts(updatedProducts);
  }

  // Menge erhöhen
  incrementSize(product: any, size: any) {
    const updatedProducts = this.products.map((p: any) => {
      if (p.modelId === product.modelId) {
        return {
          ...p,
          sizes: p.sizes.map((s: any) => {
            if (s.size === size.size) {
              return { ...s, quantity: (s.quantity || 0) + 1 };
            }
            return s;
          })
        };
      }
      return p;
    });
    this.current.addOrUpdateProducts(updatedProducts);
  }

  // Menge verringern
  decrementSize(product: any, size: any) {
    if (size.quantity <= 0) return;
    
    const updatedProducts = this.products.map((p: any) => {
      if (p.modelId === product.modelId) {
        return {
          ...p,
          sizes: p.sizes.map((s: any) => {
            if (s.size === size.size) {
              return { ...s, quantity: Math.max(0, (s.quantity || 0) - 1) };
            }
            return s;
          })
        };
      }
      return p;
    });
    this.current.addOrUpdateProducts(updatedProducts);
  }

  // Toggle Edit Mode
  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
    this.cdr.markForCheck();
  }


}
